import { Button, Container, Input } from 'components/atoms';
import { FormInput, MagellanPopups } from 'components/molecules';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useCustomParamId } from 'hooks/useParamId';
import { t } from 'i18next';
import toast from 'react-hot-toast';
import {
  PiCopy,
  PiGlobe,
  PiLink,
  PiLinkSimple,
  PiLinkSimpleBreak,
  PiNavigationArrow,
} from 'react-icons/pi';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useGetMusicProductDigital } from 'services/music-product-digital';
import { IMusicProductDigitalForm } from 'services/music-product-digital/types/form';
import {
  useAssignSmartLink,
  useCreateSmartLink,
  useGetSmartLinkDomains,
} from 'services/smartlink';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

const MusicProductDigitalSmartLink = () => {
  const productId = useCustomParamId('id', '/music-product-digital');

  const { values }: IFormikContextType<IMusicProductDigitalForm> =
    useFormikContext<IMusicProductDigitalForm>();

  const musicProductDigital = useGetMusicProductDigital(productId, false);

  const smartLinkDomains = useGetSmartLinkDomains();

  const createSmartLink = useCreateSmartLink();

  const assignSmartLink = useAssignSmartLink();

  const domainOptions = smartLinkDomains.data?.map(d => ({
    value: d.name,
    label: d.name,
  }));

  const onCreateSmartLink = async (actions: PopupActions) => {
    if (!values.marketing.domainName.length || !values.marketing.linkName) {
      return toast.error(t('error-code.FORM_ERROR'));
    }

    await createSmartLink.mutateAsync(
      {
        digitalMusicProductId: productId,
        linkName: values.marketing.linkName,
        domainName: values.marketing.domainName,
      },
      {
        onSuccess: async () => {
          await musicProductDigital.refetch();
          actions.close();
        },
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );
  };
  const onAssignSmartLink = async (actions: PopupActions) => {
    if (!values.marketing.smartlink.length) {
      return toast.error(t('error-code.FORM_ERROR'));
    }

    await assignSmartLink.mutateAsync(
      {
        digitalMusicProductId: productId,
        smartlink: values.marketing.smartlink,
      },
      {
        onSuccess: async () => {
          await musicProductDigital.refetch();
          actions.close();
        },
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );
  };

  const onCopySmartLink = () => {
    if (values.smartlink) {
      navigator.clipboard.writeText(values.smartlink);
      toast.success(
        t('toast-message.copied-what', {
          what: `${values.mainTitle.title ?? '...'} ${t('smartlink.what')}`,
        })
      );
    }
  };

  return (
    <>
      <Container className=' self-start max-w-[500px] mb-8 '>
        <Container.Header
          title={
            <>
              <PiLink />
              {values.mainTitle.title ?? '...'} {t('smartlink.what')}
            </>
          }
        />
        <Container.Content>
          <div
            className='flex gap-2 items-center w-full'
            title={values.smartlink ?? undefined}>
            <div
              className='flex items-center p-8 pr-18 w-full rounded-full text-base h-min'
              style={{ backgroundColor: 'var(--secondary-soft)' }}>
              <PiGlobe
                className='size-5 mr-2 opacity-50'
                style={{ flex: '0 0 auto' }}
              />
              <p className='line-clamp-1 break-all'>
                {values.smartlink ?? '-'}
              </p>
            </div>
            <Button
              isClear
              onlyIcon
              disabled={!values.smartlink}
              onClick={onCopySmartLink}>
              <Button.Icon icon={<PiCopy />} />
            </Button>
            <Button
              isClear
              onlyIcon
              disabled={!values.smartlink}
              onClick={() =>
                values.smartlink && window.open(values.smartlink, '_blank')
              }>
              <Button.Icon
                icon={<PiNavigationArrow transform='scale(-1,1)' />}
              />
            </Button>
          </div>
        </Container.Content>
      </Container>
      {!values.smartlink && (
        <>
          <Container className=' self-start max-w-[500px] '>
            <Container.Header
              title={
                <>
                  <PiLinkSimple />
                  {t('button.create-what', { what: t('smartlink.what') })}
                </>
              }
            />
            <Container.Content>
              <Input.ComplexWrapper sizes={[50, 50]}>
                <FormInput.Select
                  name='marketing.domainName'
                  label={t('Domain')}
                  displayMode={false}
                  isCreatable={false}
                  isDisabled={!!values.smartlink}
                  options={domainOptions}
                  placeholder={'magelup.lnk.to'}
                />
                <FormInput.Text
                  name='marketing.linkName'
                  label={t('Link')}
                  placeholder={values.mainTitle.title.replaceAll(' ', '_')}
                  disabled={!!values.smartlink}
                  displayMode={false}
                />
              </Input.ComplexWrapper>
              <div
                className='flex items-center p-8 pr-18 m-4  rounded-full text-base'
                style={{ backgroundColor: 'var(--secondary-soft)' }}>
                <PiGlobe className='size-5 mr-2 opacity-35' />
                <p className='opacity-65'>https://</p>
                {values.marketing.domainName}
                <p className='opacity-100'>
                  {!!values.marketing.domainName.length && '/'}
                  {values.marketing.linkName}
                </p>
              </div>
              <MagellanPopups.WarningPopup
                onConfirm={onCreateSmartLink}
                title={t('popup.header')}
                message={t('smartlink.popup-create', {
                  productName: values.mainTitle.title,
                  smartlink: `https://${values.marketing.domainName}/${values.marketing.linkName}`,
                })}
                confirmButtonVariant={BUTTON_VARIANT.submit}
                trigger={
                  <Button
                    fullWidth
                    variant={BUTTON_VARIANT.submit}
                    disabled={
                      !!values.smartlink || musicProductDigital.isLoading
                    }
                    isLoading={createSmartLink.isPending}>
                    <Button.Icon icon={<PiLinkSimple />} />
                    <Button.Text
                      text={t('button.create-what', {
                        what: t('smartlink.what'),
                      })}
                    />
                  </Button>
                }
              />
            </Container.Content>
          </Container>
          <Container className=' self-start max-w-[500px] mt-8'>
            <Container.Header
              title={
                <>
                  <PiLinkSimpleBreak />
                  {t('button.add-what', { what: t('smartlink.what') })}
                </>
              }
            />
            <Container.Content className='flex flex-row'>
              <FormInput.Text
                name='marketing.smartlink'
                label={t('smartlink.what')}
                disabled={!!values.smartlink}
                displayMode={false}
              />
              <MagellanPopups.WarningPopup
                onConfirm={onAssignSmartLink}
                title={t('popup.header')}
                message={t('smartlink.popup-assign', {
                  productName: values.mainTitle.title,
                  smartlink: values.marketing.smartlink,
                })}
                confirmButtonVariant={BUTTON_VARIANT.submit}
                trigger={
                  <Button
                    fullWidth
                    disabled={
                      !!values.smartlink || musicProductDigital.isLoading
                    }
                    isLoading={assignSmartLink.isPending}>
                    <Button.Icon icon={<PiLinkSimpleBreak />} />
                    <Button.Text
                      text={t('button.add-what', { what: t('smartlink.what') })}
                    />
                  </Button>
                }
              />
            </Container.Content>
          </Container>
        </>
      )}
    </>
  );
};

export default MusicProductDigitalSmartLink;
